import { useState } from "react";
import { useCall } from "@/call/CallProvider";
import { SAID } from "@/call/simulate";
import type { CallState } from "@/call/types";

/**
 * `?states`: every screen the call has, one press away.
 *
 * A development affordance, and the reason the call's states are worth keeping
 * in one object — each of these is one literal, not a sequence to act out.
 */
const STATES: Record<string, CallState> = {
  Ready: { phase: "ready", mode: "listening", turns: [], note: null },
  Connecting: { phase: "connecting", mode: "listening", turns: [], note: null },
  Talking: { phase: "live", mode: "talking", turns: SAID, note: null },
  Listening: { phase: "live", mode: "listening", turns: SAID.slice(0, 2), note: null },
  "30 seconds left": { phase: "live", mode: "listening", turns: SAID, note: "warn" },
  "End of the demo call": { phase: "ready", mode: "listening", turns: SAID, note: "end" },
  "Out of demo calls": { phase: "limit", mode: "listening", turns: [], note: "limit" },
  "Microphone blocked": { phase: "ready", mode: "listening", turns: [], note: "blocked" },
  "No microphone": { phase: "ready", mode: "listening", turns: [], note: "nomic" },
  "Couldn't connect": { phase: "ready", mode: "listening", turns: [], note: "failed" },
};

const NAMES = Object.keys(STATES);

export function StatesBar({
  opened,
  setOpened,
}: {
  opened: boolean;
  setOpened: (v: boolean) => void;
}) {
  const { set, hush, load } = useCall();
  const [pick, setPick] = useState(NAMES[0]!);

  return (
    <div className="states">
      {/* A select rather than a list, so it stays out of the way on a phone. */}
      <select
        aria-label="Call state"
        value={pick}
        onChange={(e) => {
          const name = e.target.value;
          setPick(name);
          hush();
          load();
          set(STATES[name]!);
        }}
      >
        {NAMES.map((n) => (
          <option key={n}>{n}</option>
        ))}
      </select>
      <button
        type="button"
        className={opened ? "on" : undefined}
        onClick={() => {
          load();
          setOpened(!opened);
        }}
      >
        Floating panel
      </button>
    </div>
  );
}
